import { isPiiPropertyKey, type AnalyticsRecord } from "./privacy";
import { scrubAnalyticsProps } from "./propsAllowlist";

export interface SentryUser {
  id?: string;
  ip_address?: string;
  geo?: AnalyticsRecord;
  [key: string]: unknown;
}

export interface SentryBreadcrumb {
  category?: string;
  message?: string;
  data?: AnalyticsRecord;
  [key: string]: unknown;
}

export interface SentryEvent {
  user?: SentryUser;
  request?: AnalyticsRecord;
  contexts?: AnalyticsRecord;
  server_name?: string;
  breadcrumbs?: { values?: SentryBreadcrumb[] } | SentryBreadcrumb[];
  [key: string]: unknown;
}

const BREADCRUMB_DATA_DENYLIST: readonly string[] = [
  "url",
  "from",
  "to",
  "href",
  "input",
  "arguments",
];

/** Contexts attached by default that reproduce a device fingerprint. */
const PII_CONTEXT_KEYS: readonly string[] = ["os", "browser", "device", "culture"];

function isScrubbedBreadcrumbKey(key: string): boolean {
  return BREADCRUMB_DATA_DENYLIST.includes(key) || isPiiPropertyKey(key);
}

/**
 * Removes personal-data keys from a free-form property bag before it is attached
 * to a Sentry event. Applied both to breadcrumb `data` and to the `extra` block
 * that carries error diagnostics, so the two paths cannot diverge.
 */
export function scrubSentryProps(props: AnalyticsRecord): AnalyticsRecord {
  const kept = Object.entries(props).filter(([key]) => !isScrubbedBreadcrumbKey(key));
  return Object.fromEntries(kept);
}

function scrubsBreadcrumbMessage(category: string | undefined): boolean {
  return category === "console" || (category?.startsWith("ui.") ?? false);
}

/**
 * Sentry `beforeBreadcrumb` hook. Removes URL- and value-bearing data keys
 * (navigation/fetch/xhr embed full URLs and request bodies; console breadcrumbs
 * embed the logged arguments) while keeping non-personal diagnostics such as
 * method and status code. For console and DOM (`ui.*`) breadcrumbs the free-text
 * message is itself redacted, since it carries logged content or selectors.
 */
export function scrubSentryBreadcrumb(crumb: SentryBreadcrumb): SentryBreadcrumb {
  const next: SentryBreadcrumb = { ...crumb };
  if (next.data) {
    next.data = scrubAnalyticsProps(next.data);
  }
  if (scrubsBreadcrumbMessage(next.category) && next.message !== undefined) {
    next.message = "[redacted]";
  }
  return next;
}

export interface SentryPrivacyOptions {
  sendDefaultPii: false;
  beforeSend: (event: SentryEvent) => SentryEvent;
  beforeBreadcrumb: (crumb: SentryBreadcrumb) => SentryBreadcrumb;
}

/**
 * Privacy-hardened Sentry init options: `sendDefaultPii` explicitly off, plus
 * `beforeSend` / `beforeBreadcrumb` scrubbers that strip the request block, the
 * user IP, fingerprinting contexts, the host name, and URLs from breadcrumbs.
 */
export function sentryPrivacyOptions(): SentryPrivacyOptions {
  return {
    sendDefaultPii: false,
    beforeSend: scrubSentryEvent,
    beforeBreadcrumb: scrubSentryBreadcrumb,
  };
}

/**
 * Sentry `beforeSend` hook. Drops the `request` block (URL plus User-Agent and
 * Referer headers that the HttpContext integration attaches even when
 * `sendDefaultPii` is false), the host name (`server_name`), the user IP and
 * IP-derived geolocation, and the fingerprinting contexts (os / browser /
 * device / culture); it also scrubs URLs from breadcrumbs and personal data
 * from `extra`. The anonymous user id is preserved.
 *
 * Note: `user.geo` is added by Sentry server-side from the request IP after this
 * hook has run, so stripping it here is defence-in-depth only. The authoritative
 * fix is an Advanced Data Scrubbing rule `[Remove] [Anything] from [$user.geo.**]`
 * in the project settings - the "Prevent Storing of IP Addresses" toggle alone
 * removes the IP but keeps the derived geolocation.
 */
export function scrubSentryEvent(event: SentryEvent): SentryEvent {
  const { request: _request, server_name: _serverName, user, contexts, breadcrumbs, ...rest } =
    event;
  const next: SentryEvent = { ...rest };
  if (next.extra) {
    next.extra = scrubSentryProps(next.extra as AnalyticsRecord);
  }
  if (user) {
    const { ip_address: _ip, geo: _geo, ...safeUser } = user;
    next.user = safeUser;
  }
  if (contexts) {
    const kept = Object.entries(contexts).filter(([key]) => !PII_CONTEXT_KEYS.includes(key));
    next.contexts = Object.fromEntries(kept);
  }
  if (Array.isArray(breadcrumbs)) {
    next.breadcrumbs = breadcrumbs.map(scrubSentryBreadcrumb);
  } else if (breadcrumbs) {
    next.breadcrumbs = {
      ...breadcrumbs,
      values: (breadcrumbs.values ?? []).map(scrubSentryBreadcrumb),
    };
  }
  return next;
}
